import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { itemService } from "../../api/services/itemService";
import { getCartItems, saveCartItems } from "../../utils/cartUtils";
import defaultProductImage from "../../images/Default-Image.jpeg";
import "./NewArrival.css";

const NewArrival = () => {
  const navigate = useNavigate();
  const [newArrivalProducts, setNewArrivalProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleShopNow = (product) => {
    if (!product || product.isComingSoon) return;

    const cartItems = getCartItems();
    const existingItemIndex = cartItems.findIndex((item) => item.id === product.id);

    if (existingItemIndex >= 0) {
      cartItems[existingItemIndex].quantity =
        (cartItems[existingItemIndex].quantity || 1) + 1;
    } else {
      cartItems.push({
        id: product.id,
        name: product.name,
        price: product.price || 0,
        quantity: 1,
        image: product.image || defaultProductImage,
        availableQty: product.availableQty || 0,
        description: product.description || "",
      });
    }

    saveCartItems(cartItems);
    navigate("/checkout");
  };

  // Helper function to get full image URL using API base URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return "";
    const baseUrl = "https://wireox.erp.gold";
    
    // If it's already a full URL, return as is
    if (
      typeof imagePath === "string" &&
      (imagePath.startsWith("http://") || imagePath.startsWith("https://"))
    ) {
      return imagePath;
    }
    
    // If it's a relative path starting with /, prepend the base URL
    if (typeof imagePath === "string" && imagePath.startsWith("/")) {
      return `${baseUrl}${imagePath}`;
    }
    
    // If it's a relative path without leading /, prepend base URL with /
    if (typeof imagePath === "string" && imagePath.trim() !== "") {
      return `${baseUrl}/${imagePath}`;
    }
    
    return "";
  };

  useEffect(() => {
    const fetchNewArrivals = async () => {
      try {
        setLoading(true);
        // tagName = "New Arrival" for New Arrival products
        const response = await itemService.getItemsWithTagList("New Arrival");

        // Transform API response to match component structure
        // API response structure: { Pagination: {...}, Items: [...] }
        let products = [];
        const items =
          response?.Items ||
          response?.items ||
          (Array.isArray(response) ? response : []);

        if (items && Array.isArray(items) && items.length > 0) {
          products = items.map((item, index) => {
            // Get image from Images array if available from API
            let imageUrl = "";
            if (
              item.Images &&
              Array.isArray(item.Images) &&
              item.Images.length > 0
            ) {
              // Handle both object format {ImagePath, AltAttribute} and string format
              const firstImage = item.Images[0];
              if (typeof firstImage === "object" && firstImage.ImagePath) {
                imageUrl = getImageUrl(firstImage.ImagePath);
              } else if (typeof firstImage === "string") {
                imageUrl = getImageUrl(firstImage);
              }
            }

            return {
              id: item.ItemId || item.id || index + 1,
              name: item.ItemName || item.Name || "LCD Assembly",
              variant:
                item.PartNumber || item.ItemDescription || "Black and White",
              image: imageUrl || defaultProductImage,
              alt: item.ItemName || item.Name || `Product ${index + 1}`,
              isLarge: index === 0, // First item is large
              price: item.UnitPrice || item.DiscountPrice || 0,
              availableQty: item.AvailableQty || 0,
              description: item.ItemDescription || "",
            };
          });
        }

        // Always ensure we have 4 slots, but mark missing ones as "coming soon"
        const totalSlots = 4;
        const actualProducts = products.slice(0, totalSlots);

        // Fill remaining slots with "coming soon" placeholders
        while (actualProducts.length < totalSlots) {
          actualProducts.push({
            id: `coming-soon-${actualProducts.length}`,
            name: "Coming Soon",
            variant: "",
            image: "",
            alt: "Coming Soon",
            isLarge: actualProducts.length === 0,
            isComingSoon: true,
          });
        }

        setNewArrivalProducts(actualProducts);
      } catch (error) {
        console.error("Error fetching new arrivals:", error);
        // Fallback to "Coming Soon" for all cards if API fails
        setNewArrivalProducts([
          {
            id: "coming-soon-0",
            name: "Coming Soon",
            variant: "",
            image: "",
            alt: "Coming Soon",
            isLarge: true,
            isComingSoon: true,
          },
          {
            id: "coming-soon-1",
            name: "Coming Soon",
            variant: "",
            image: "",
            alt: "Coming Soon",
            isLarge: false,
            isComingSoon: true,
          },
          {
            id: "coming-soon-2",
            name: "Coming Soon",
            variant: "",
            image: "",
            alt: "Coming Soon",
            isLarge: false,
            isComingSoon: true,
          },
          {
            id: "coming-soon-3",
            name: "Coming Soon",
            variant: "",
            image: "",
            alt: "Coming Soon",
            isLarge: false,
            isComingSoon: true,
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchNewArrivals();
  }, []);

  if (loading) {
    return null;
  }

  if (!newArrivalProducts || newArrivalProducts.length === 0) {
    return null;
  }

  return (
    <div className="na-new-arrival-section">
      <div className="na-new-arrival-container">
        <div className="na-new-arrival-header">
          <div className="na-new-arrival-title-bar"></div>
          <h2 className="na-new-arrival-title">New Arrival</h2>
        </div>

        <div className="na-new-arrival-grid">
          {/* Large left card */}
          {newArrivalProducts[0] && (
            <div className="na-new-arrival-item na-large-card">
              <div className="na-item-image-container">
                <div className="na-placeholder-image">
                  <img
                    src={newArrivalProducts[0].image || defaultProductImage}
                    alt={newArrivalProducts[0].alt}
                    className="na-iphone-image"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = defaultProductImage;
                    }}
                  />
                </div>
                <div className="na-image-info">
                  <div
                    className={`na-product-name ${
                      newArrivalProducts[0].isComingSoon ? "na-coming-soon" : ""
                    }`}
                  >
                    {newArrivalProducts[0].name}
                  </div>
                  {!newArrivalProducts[0].isComingSoon && (
                    <>
                      <div
                        className="na-product-variant"
                        title={newArrivalProducts[0].variant}
                      >
                        {newArrivalProducts[0].variant}
                      </div>
                      <button
                        className="na-shop-now-btn"
                        onClick={() => handleShopNow(newArrivalProducts[0])}
                      >
                        SHOP NOW
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Right side grid - 1-2 layout */}
          <div className="na-right-grid">
            {/* First row: 1 card (full width) */}
            {newArrivalProducts[1] && (
              <div className="na-new-arrival-item na-small-card na-first-row">
                <div className="na-item-image-container">
                  <div className="na-placeholder-image">
                    <img
                      src={newArrivalProducts[1].image || defaultProductImage}
                      alt={newArrivalProducts[1].alt}
                      className="na-iphone-image"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = defaultProductImage;
                      }}
                    />
                  </div>
                  <div className="na-image-info">
                    <div
                      className={`na-product-name ${
                        newArrivalProducts[1].isComingSoon
                          ? "na-coming-soon"
                          : ""
                      }`}
                    >
                      {newArrivalProducts[1].name}
                    </div>
                    {!newArrivalProducts[1].isComingSoon && (
                      <>
                        <div
                          className="na-product-variant"
                          title={newArrivalProducts[1].variant}
                        >
                          {newArrivalProducts[1].variant}
                        </div>
                        <button
                          className="na-shop-now-btn"
                          onClick={() => handleShopNow(newArrivalProducts[1])}
                        >
                          SHOP NOW
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Second row: 2 cards side by side */}
            <div className="na-second-row-container">
              {newArrivalProducts[2] && (
                <div className="na-new-arrival-item na-small-card na-second-row">
                  <div className="na-item-image-container">
                    <div className="na-placeholder-image">
                      <img
                        src={newArrivalProducts[2].image || defaultProductImage}
                        alt={newArrivalProducts[2].alt}
                        className="na-iphone-image"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = defaultProductImage;
                        }}
                      />
                    </div>
                    <div className="na-image-info">
                      <div
                        className={`na-product-name ${
                          newArrivalProducts[2].isComingSoon
                            ? "na-coming-soon"
                            : ""
                        }`}
                      >
                        {newArrivalProducts[2].name}
                      </div>
                      {!newArrivalProducts[2].isComingSoon && (
                        <>
                          <div
                            className="na-product-variant"
title={newArrivalProducts[2].variant}
                            >
                              {newArrivalProducts[2].variant}
                          </div>
                          <button
                            className="na-shop-now-btn"
                            onClick={() => handleShopNow(newArrivalProducts[2])}
                          >
                            SHOP NOW
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {newArrivalProducts[3] && (
                <div className="na-new-arrival-item na-small-card na-second-row">
                  <div className="na-item-image-container">
                    <div className="na-placeholder-image">
                      <img
                        src={newArrivalProducts[3].image || defaultProductImage}
                        alt={newArrivalProducts[3].alt}
                        className="na-iphone-image"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = defaultProductImage;
                        }}
                      />
                    </div>
                    <div className="na-image-info">
                      <div
                        className={`na-product-name ${
                          newArrivalProducts[3].isComingSoon
                            ? "na-coming-soon"
                            : ""
                        }`}
                      >
                        {newArrivalProducts[3].name}
                      </div>
                      {!newArrivalProducts[3].isComingSoon && (
                        <>
                          <div
                            className="na-product-variant"
title={newArrivalProducts[3].variant}
                            >
                              {newArrivalProducts[3].variant}
                          </div>
                          <button
                            className="na-shop-now-btn"
                            onClick={() => handleShopNow(newArrivalProducts[3])}
                          >
                            SHOP NOW
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewArrival;
